import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import PartnerEntry from '../components/PartnerEntry';

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About"
        title="Built by people who already know"
        emphasis="this industry."
        lede="OfficerCapital was built by the team behind OfficerApps, the operating platform used by hundreds of security guard companies in North America. We saw firsthand how hard it was for owners to find the right capital partner — and how often the wrong one cost them. So we built the marketplace we wished existed."
      />

      {/* THE OFFICERAPPS CONNECTION */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">The OfficerApps connection</div>
              <h2 className="oc-h2">
                A separate product, with <em className="oc-em">shared roots</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                OfficerCapital is a standalone marketplace — your data here is yours, separate from any OfficerApps account
                you may have. But our origin in the operating side of the industry is what makes the marketplace work.
                We know which numbers matter to acquirers because we know which numbers matter to operators.
              </p>
              <p className="oc-body oc-mt-3 oc-max-440">
                If you're an OfficerApps customer, your listings get a priority badge in capital partner search results,
                and partners pay 50% less to unlock your file — meaning more of them do.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <Stat
                  num="500+"
                  label="Guard companies use OfficerApps"
                />
                <hr className="oc-rule" />
                <Stat
                  num="$2B+"
                  label="In billable hours processed annually"
                />
                <hr className="oc-rule" />
                <Stat
                  num="11"
                  label="Years building software for the industry"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-mb-5">
            <div className="oc-eyebrow-gray oc-mb-2">Principles</div>
            <h2 className="oc-h2">
              How we'll <em className="oc-em">decide things</em>.
            </h2>
          </div>

          <div className="oc-grid-3-wide">
            <Principle
              num="01"
              title="Owners come first"
              body="The marketplace exists for guard company owners. When a decision pits owner interests against partner convenience, owners win."
            />
            <Principle
              num="02"
              title="Anonymity is a feature, not a perk"
              body="We will not weaken the access controls. We will not surface owner identity to drive partner engagement. We will not negotiate this."
            />
            <Principle
              num="03"
              title="No middleman fees"
              body="We don't take a cut of your financing, your investment, or your sale. Partners pay us. Owners don't. That stays."
            />
            <Principle
              num="04"
              title="Quality over volume"
              body="We'd rather have 50 active partners who close than 500 who browse. Vetting is manual. It will stay manual."
            />
            <Principle
              num="05"
              title="Industry-only"
              body="OfficerCapital is for security guard companies. We're not expanding into other service businesses. The expertise here is the moat."
            />
            <Principle
              num="06"
              title="No selling your data"
              body="Listing data does not get sold, syndicated, or shared with anyone outside the approved partner channel. If we ever change this policy, we will tell you first and you can pull your listing."
            />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Reach us</div>
              <h2 className="oc-h2">
                Questions before you <em className="oc-em">list</em>?
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                The fastest way to get a real answer is to email us. We're a small team and we read every message.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ContactRow label="Owners" detail="hello@officercapital.com" />
                <hr className="oc-rule" />
                <ContactRow label="Capital partners" detail="partners@officercapital.com" />
                <hr className="oc-rule" />
                <ContactRow label="Press" detail="press@officercapital.com" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function Stat({ num, label }) {
  return (
    <div>
      <div className="oc-stat-num" style={{ fontSize: 36 }}>{num}</div>
      <div className="oc-stat-label" style={{ marginTop: 6 }}>{label}</div>
    </div>
  );
}

function Principle({ num, title, body }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 16 }}>
        <span className="oc-step__num">{num}</span>
        <hr className="oc-rule" style={{ flex: 1, marginTop: 12 }} />
      </div>
      <h4 className="oc-h4" style={{ marginBottom: 8 }}>{title}</h4>
      <p className="oc-body-sm">{body}</p>
    </div>
  );
}

function ContactRow({ label, detail }) {
  return (
    <div className="oc-row-sb" style={{ alignItems: 'baseline' }}>
      <span style={{ fontSize: 14, color: 'var(--oc-ink)', fontWeight: 500 }}>{label}</span>
      <a href={`mailto:${detail}`} style={{ fontSize: 14, color: 'var(--oc-green-deep)' }}>{detail}</a>
    </div>
  );
}
